import {
  BackgroundImage,
  Box,
  List,
  Overlay,
  Tabs,
  type TabsProps,
  Text,
  Title,
  rem,
} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {type FC, useState} from 'react';
import {TITLE_SUFFIX} from '../../../config/constants';
import {COLOR_BLACK_2} from '../../../config/constants/colorCodes';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  REPORT_ANALYTICS_ABOUT_1,
  REPORT_ANALYTICS_ABOUT_2,
  REPORT_ANALYTICS_ABOUT_3,
  REPORT_ANALYTICS_BACKGROUND,
  REPORT_ANALYTICS_LOGO,
  REPORT_ANALYTICS_OGP_IMAGE,
} from '../../../config/constants/img';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {TabsPanel} from './Presenter/TabsPanel';
import {TopicsCards} from './Presenter/TopicsCard';

type Props = Record<string, never>;

function MaintenanceOverlay() {
  return (
    <Overlay
      blur={2}
      opacity={0.4}
      color={COLOR_BLACK_2}
      center
      zIndex={10}
      role="alert"
      aria-live="polite"
    >
      <Box className="bg-white rounded-16 p-32 w-full max-w-[500px] mx-16">
        <Text className="text-md text-black-1 text-center">
          AI選考対策はメンテナンス中です。 <br />
          しばらくお待ちください。
        </Text>
      </Box>
    </Overlay>
  );
}

function StyledTabs(props: TabsProps) {
  return (
    <Tabs
      unstyled
      styles={(theme) => ({
        tab: {
          ...theme.fn.focusStyles(),
          backgroundColor: theme.white,
          color: '#676CFA',
          border: `${rem(1)} solid #676CFA`,
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          width: '100%',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: theme.fontSizes.sm,
          display: 'flex',
          alignItems: 'center',

          '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
          },

          '&:not(:first-of-type)': {
            borderLeft: 0,
          },

          '&:first-of-type': {
            borderTopLeftRadius: theme.radius.md,
            borderBottomLeftRadius: theme.radius.md,
          },

          '&:last-of-type': {
            borderTopRightRadius: theme.radius.md,
            borderBottomRightRadius: theme.radius.md,
          },

          '&[data-active]': {
            backgroundColor: '#676CFA',
            borderColor: '#676CFA',
            color: theme.white,
          },
        },

        tabsList: {
          display: 'flex',
        },
      })}
      {...props}
    />
  );
}

export const ReportAnalyticsSearchPage: FC<Props> = () => {
  const router = useRouter();
  const {isPC} = useMediaQuery();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const [activeTab, setActiveTab] = useState<string>('intern');
  const [companiesCount, setCompaniesCount] = useState<number>(0);

  const isMaintenance = false; // メンテナンス対応が必要になったら切り替え

  return (
    <>
      <Head>
        <title key="title">{`AI選考対策${TITLE_SUFFIX}`}</title>
        <meta
          key="og:title"
          property="og:title"
          content="AI選考対策｜外資就活ドットコム"
        />

        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={`${GSSKT_HOST}${REPORT_ANALYTICS_OGP_IMAGE}`}
        />
        <meta
          key="description"
          name="description"
          content="外資就活の膨大な選考体験記データをベースにAIが企業ごとの選考体験記をサマライズ。気になっている企業の選考フローや、想定質問、選考突破した先輩からのアドバイスをまとめて生成します！"
        />
        <meta
          key="og:description"
          property="og:description"
          content="外資就活の膨大な選考体験記データをベースにAIが企業ごとの選考体験記をサマライズ。気になっている企業の選考フローや、想定質問、選考突破した先輩からのアドバイスをまとめて生成します！"
        />

        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,コラム,ES・体験記,投資銀行,コンサル,商社"
        />
        <meta
          key="twitter:title"
          name="twitter:title"
          content="AI選考対策｜外資就活ドットコム"
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={REPORT_ANALYTICS_OGP_IMAGE}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content="外資就活の膨大な選考体験記データをベースにAIが企業ごとの選考体験記をサマライズ。気になっている企業の選考フローや、想定質問、選考突破した先輩からのアドバイスをまとめて生成します！"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
      </Head>
      <Box className="flex flex-col pc:w-[1200px] w-full items-center bg-white">
        <Box className="flex flex-col pc:flex-row items-center p-16 pc:gap-12">
          <Text className="text-base text-black-1 font-bold pc:text-md">
            【アップデート】
          </Text>
          <Text className="text-base text-black-1">2026/01/15</Text>
          <List className="text-base text-black-1 list-disc list-inside px-24">
            <List.Item className="leading-sm mb-4">
              データの更新を行いました
            </List.Item>
          </List>
        </Box>
      </Box>
      <Box className="relative pc:w-[1200px] w-full">
        {isMaintenance && <MaintenanceOverlay />}
        <BackgroundImage
          src={REPORT_ANALYTICS_BACKGROUND}
          className="flex flex-col w-full items-center justify-center pc:p-32 p-16"
          aria-hidden={isMaintenance || undefined}
        >
          {isPC ? (
            <Box className="w-fit bg-aiReports-accent mt-8 px-8 py-4">
              <Text className="text-md font-bold text-aiReports text-center">
                本選考/インターンの対策方針の策定や、選考直前の振り返りに！
              </Text>
            </Box>
          ) : (
            <>
              <Box className="w-fit bg-aiReports-accent mt-8 px-8 py-4">
                <Text className="text-md font-bold text-aiReports text-center">
                  本選考/インターンの対策方針の策定や、
                </Text>
              </Box>
              <Box className="w-fit bg-aiReports-accent mt-8 px-8 py-4">
                <Text className="text-md font-bold text-aiReports text-center">
                  選考直前の振り返りに！
                </Text>
              </Box>
            </>
          )}
          <h1>
            <img
              src={REPORT_ANALYTICS_LOGO}
              className="w-[220px] h-[82px] mt-16"
              alt="AI選考対策 企業のインターン/本選考データをAIが分析"
            />
          </h1>
          <Text className="w-fit text-center mt-24 text-md text-white pc:mx-0 mx-16">
            本選考経験者やインターン参加者の選考体験データをAIが分析し、
            {isPC && <br />}
            企業 × 職種別に面接内容や選考突破した先輩のアドバイスを生成します
          </Text>
          <Box className="bg-white rounded-[24px] pc:w-[600px] w-full pc:p-32 p-24 flex flex-col items-center mt-24">
            <StyledTabs
              value={activeTab}
              onTabChange={(value) => {
                if (value) setActiveTab(value);
              }}
            >
              <Tabs.List className="w-full justify-center font-bold">
                <Tabs.Tab value="selection" className="relative">
                  <Box className="absolute top-[-12px] left-[30%] bg-accent-2 text-white text-sm px-12 rounded-16">
                    <Text>UPDATE</Text>
                  </Box>
                  <Text>本選考</Text>
                </Tabs.Tab>
                <Tabs.Tab value="intern" className="relative">
                  <Box className="absolute top-[-12px] left-[30%] bg-accent-2 text-white text-sm px-12 rounded-16">
                    <Text>UPDATE</Text>
                  </Box>
                  <Text>インターン</Text>
                </Tabs.Tab>
              </Tabs.List>
              <TabsPanel
                reportType="selection"
                isActive={activeTab === 'selection'}
                setCompaniesCount={setCompaniesCount}
              />
              <TabsPanel
                reportType="intern"
                isActive={activeTab === 'intern'}
                setCompaniesCount={setCompaniesCount}
              />
            </StyledTabs>
          </Box>
        </BackgroundImage>
        <Box
          className="pc:w-[1200px] w-full bg-white"
          aria-hidden={isMaintenance || undefined}
        >
          <Box className="text-center p-12 mt-16">
            <Text className="text-xl text-black-1 font-bold">
              難関企業 {companiesCount}社の
              {activeTab === 'selection' ? '本' : 'インターン'}選考対策に対応
            </Text>
          </Box>
          <TopicsCards isPC={isPC} reportType={activeTab} />
        </Box>
      </Box>
      <Box className="flex flex-col pc:w-[1200px] w-full items-center justify-center pc:p-64 p-24 bg-white">
        <Title className="text-xl text-black font-bold pc:mt-0 mt-32" order={2}>
          AI選考対策とは？
        </Title>
        <Text className="text-md text-black-1 pc:text-center mt-16">
          現在も外資就活に寄せられ続けている、膨大な選考体験記データをベースに
          {isPC && <br />}
          AIが企業ごとの選考体験記をサマライズ。
          <br />
          気になっている企業の選考フローや、想定質問、選考突破した先輩からのアドバイスをまとめて生成します！
        </Text>
        <Text className="text-base text-black-2 mt-8">
          ※この機能はβ版です。順次改善を進めていきます。
        </Text>
        <Box className="pc:flex mt-64 justify-between pc:w-[1008px]">
          <Box className="flex-1 pc:mr-40 flex flex-col items-center">
            <img
              src={REPORT_ANALYTICS_ABOUT_1}
              className="w-[235px] h-[200px]"
            />
            <Text className="text-xl text-black text-center font-bold mt-16">
              志望企業の選考フロー確認に
            </Text>
            <Text className="text-md text-black-1 text-center mt-8">
              選考分析データを元に、自分のレベルに合っているかを確認。エントリー指針の一つに！
            </Text>
          </Box>
          <Box className="flex-1 pc:mt-0 mt-40 flex flex-col items-center">
            <img
              src={REPORT_ANALYTICS_ABOUT_2}
              className="w-[235px] h-[200px]"
            />
            <Text className="text-xl text-black text-center font-bold mt-16">
              効率的な選考研究
            </Text>
            <Text className="text-md text-black-1 text-center mt-8">
              AIが要約してくれるので、志望業界の選考データをすべてチェックする必要はありません！
            </Text>
          </Box>
          <Box className="flex-1 pc:mt-0 mt-40 pc:ml-40 flex flex-col items-center">
            <img
              src={REPORT_ANALYTICS_ABOUT_3}
              className="w-[235px] h-[200px]"
            />
            <Text className="text-xl text-black text-center font-bold mt-16">
              選考前の最終チェックに
            </Text>
            <Text className="text-md text-black-1 text-center mt-8">
              生成サマリは体験記が増えるごとにアップデートされていきます。URLをブックマークして、選考前に見返しましょう！
            </Text>
          </Box>
        </Box>
      </Box>
    </>
  );
};
